// B3-03: one shared switch the whole platform can ask "are we exploring with
// sample data right now?", plus the single friendly message shown whenever
// somebody tries to change or download something while exploring.
//
// The database refuses these writes on its own (see the explore sandbox
// triggers and read-only policies), so this layer exists purely so the person
// gets a warm explanation instead of a raw failure.

export const EXPLORE_TOAST_TITLE = "You are just exploring";

export const EXPLORE_BLOCK_MESSAGE =
  "This is a sample nursery for exploring. Upgrading unlocks full editing, and real exports, for your own nursery.";


/** Optional second line shown under the message at a few high-value moments. */
export type ExploreFollowUp = string | undefined;

let exploring = false;
let notifier: (follow?: ExploreFollowUp) => void = () => {};
let lastBlockAt = 0;

/**
 * True when the friendly "you are just exploring" message has only just been
 * shown. Pages then skip their own generic "could not save" wording, so the
 * person sees one calm explanation rather than a failure.
 */
export function justBlockedByExplore(withinMs = 4000): boolean {
  return exploring && Date.now() - lastBlockAt < withinMs;
}


export class ExploreBlockedError extends Error {
  constructor() {
    super(EXPLORE_BLOCK_MESSAGE);
    this.name = "ExploreBlockedError";
  }
}

export function setExploringNow(value: boolean) {
  exploring = value;
}

export function isExploringNow(): boolean {
  return exploring;
}

export function setExploreNotifier(fn: (follow?: ExploreFollowUp) => void) {
  notifier = fn;
}

/**
 * Shows the friendly message when exploring and returns true so the caller can
 * stop. Returns false on a real account, where everything works as normal.
 */
export function blockIfExploring(follow?: ExploreFollowUp): boolean {
  if (!exploring) return false;
  lastBlockAt = Date.now();
  notifier(follow);
  return true;
}


/** Same as blockIfExploring, but for code paths that must not continue. */
export function assertNotExploring(follow?: ExploreFollowUp) {
  if (blockIfExploring(follow)) throw new ExploreBlockedError();
}

/** Database and remote calls that only read, so they stay allowed. */
const READ_ONLY_RPCS = new Set([
  "explore_status",
  "exit_explore_mode",
  "in_explore_mode",
  "am_i_dsl",
  "is_dsl",
  "is_front_desk",
  "has_role",
  "my_safeguarding_capability",
  "my_zone_ids",
  "nursery_owner_count",
  "nursery_owner_user_ids",
  "safeguarding_passcode_status",
  "safeguarding_records_for_admin",
  "succession_emergency_context",
  "kiosk_get_context",
  "kiosk_staff_search",
  "kiosk_staff_status",
  "kiosk_staff_today_for_admin",
  "kiosk_staff_range_for_admin",
  "can_view_child_audit",
  "child_in_my_zone",
  "owner_inactivity_scan",
  "ad_hoc_signins_pending",
  "can_edit_rota_zone",
]);

export function isReadOnlyRpc(name: string): boolean {
  return READ_ONLY_RPCS.has(name) || name.startsWith("get_");
}
